import  { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Layout from "../components/layout/CompliceLayout";
import { Pie, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";

// Register Chart.js components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

const CompliceMitigationStrategies = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState(null);

  // Sample Risk Data
  const riskCategories = {
    "Financial Risks": 35,
    "Compliance Risks": 25,
    "Operational Risks": 20,
    "Cybersecurity Risks": 15,
    "Reputational Risks": 5,
  };

  const mitigationStrategies = [
    {
      id: 1,
      category: "Financial Risks",
      title: "Advanced Financial Controls",
      description:
        "Implement multi-layer financial approval processes and real-time transaction monitoring.",
      impactScore: 8.5,
      probabilityReduction: 75,
    },
    {
      id: 2,
      category: "Compliance Risks",
      title: "Regulatory Compliance Framework",
      description:
        "Develop comprehensive compliance tracking and automated reporting systems.",
      impactScore: 7.9,
      probabilityReduction: 68,
    },
    {
      id: 3,
      category: "Operational Risks",
      title: "Process Automation",
      description:
        "Implement AI-driven process automation to reduce human error and standardize workflows.",
      impactScore: 8.2,
      probabilityReduction: 72,
    },
  ];

  // Pie Chart Data for Risk Categories
  const pieChartData = {
    labels: Object.keys(riskCategories),
    datasets: [
      {
        data: Object.values(riskCategories),
        backgroundColor: [
          "#FF6384",
          "#36A2EB",
          "#FFCE56",
          "#4BC0C0",
          "#9966FF",
        ],
      },
    ],
  };

  // Bar Chart Data for Mitigation Strategies
  const barChartData = {
    labels: mitigationStrategies.map((strategy) => strategy.title),
    datasets: [
      {
        label: "Impact Score",
        data: mitigationStrategies.map((strategy) => strategy.impactScore),
        backgroundColor: "rgba(75, 192, 192, 0.6)",
      },
      {
        label: "Probability Reduction",
        data: mitigationStrategies.map(
          (strategy) => strategy.probabilityReduction
        ),
        backgroundColor: "rgba(255, 99, 132, 0.6)",
      },
    ],
  };

  const handleOpenStrategyDetails = (strategy) => {
    setSelectedStrategy(strategy);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedStrategy(null);
  };
  return (
    <Layout>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Risk Mitigation Strategies
        </Typography>

        <Grid container spacing={3}>
          {/* Risk Distribution Pie Chart */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6">Risk Category Distribution</Typography>
                <Pie data={pieChartData} />
              </CardContent>
            </Card>
          </Grid>

          {/* Mitigation Strategies Bar Chart */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6">
                  Mitigation Strategy Performance
                </Typography>
                <Bar data={barChartData} />
              </CardContent>
            </Card>
          </Grid>

          {/* Mitigation Strategies List */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6">
                  Active Mitigation Strategies
                </Typography>
                {mitigationStrategies.map((strategy) => (
                  <Box
                    key={strategy.id}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                      p: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="subtitle1">
                      {strategy.title} ({strategy.category})
                    </Typography>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => handleOpenStrategyDetails(strategy)}
                    >
                      View Details
                    </Button>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Strategy Details Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          {selectedStrategy && (
            <>
              <DialogTitle>{selectedStrategy.title}</DialogTitle>
              <DialogContent>
                <Typography variant="body1" gutterBottom>
                  <strong>Category:</strong> {selectedStrategy.category}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Description:</strong> {selectedStrategy.description}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Impact Score:</strong> {selectedStrategy.impactScore}
                  /10
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Probability Reduction:</strong>{" "}
                  {selectedStrategy.probabilityReduction}%
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog} color="primary">
                  Close
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Box>
    </Layout>
  );
};

export default CompliceMitigationStrategies;
